import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { cx } from '@platform/core/lib/cx';
import type { IPartyCalendarMonthProps } from './IPartyCalendarMonthProps';
import { PartyCalendarLegend } from './PartyCalendarLegend';
import { PartyEventChip } from './PartyEventChip';
import { monthCells, monthEventCount, shiftMonth } from './partyCalendarModel';
import { monthTitle } from './partyDates';

/** Скільки подій показує клітинка (прототип v4: три, далі «ще N»). */
const PER_DAY = 3;

/**
 * Місяць календаря партії (прототип v4 pcal): картка з назвою місяця (20px) і кількістю видимих подій місяця (моноширинним), кнопки
 * «‹ Сьогодні ›», легенда типів і сітка 7 × 6 від понеділка (заголовки днів 11px великими літерами; клітинка ≥104px: число, до трьох подій,
 * «ще N»; дні іншого місяця — на --surface2 приглушено; сьогодні — --accent-soft з рамкою акценту).
 */
export function PartyCalendarMonth(p: IPartyCalendarMonthProps) {
  const { t } = useTranslation();
  const cells = useMemo(() => monthCells(p.month, p.visible, p.today), [p.month, p.visible, p.today]);
  const counts = useMemo(() => {
    const out: Record<string, number> = {};
    for (const e of p.events) out[e.type.key] = (out[e.type.key] ?? 0) + 1;
    return out;
  }, [p.events]);
  const weekdays = t('modBody.pcal.weekdays').split(' ');
  return (
    <section className="pcal__main">
      <div className="pcal__head">
        <div className="pcal__title">
          <strong className="pcal__month">{monthTitle(p.month, p.lang)}</strong>
          <span className="pcal__count">{monthEventCount(p.visible, p.month)}</span>
        </div>
        <div className="pcal__nav">
          <button type="button" className="pcal__arrow" aria-label={t('modBody.pcal.prev')} onClick={() => p.onMonth(shiftMonth(p.month, -1))}>‹</button>
          <button type="button" className="pcal__today" onClick={() => p.onMonth(null)}>{t('modBody.pcal.today')}</button>
          <button type="button" className="pcal__arrow" aria-label={t('modBody.pcal.next')} onClick={() => p.onMonth(shiftMonth(p.month, 1))}>›</button>
        </div>
      </div>
      <PartyCalendarLegend types={p.types} counts={counts} off={p.off} onToggle={p.onToggle} />
      <div className="pcal__grid">
        {weekdays.map((w) => <div key={w} className="pcal__wd">{w}</div>)}
        {cells.map((c) => (
          <div key={c.iso} className={cx('pcal__cell', !c.inMonth && 'is-out', c.today && 'is-today')}>
            <span className="pcal__day">{c.day}</span>
            {c.events.slice(0, PER_DAY).map((e) => <PartyEventChip key={`${e.type.key}:${e.record.id}`} event={e} onOpen={p.onOpen} />)}
            {c.events.length > PER_DAY && <span className="pcal__more">{t('modBody.pcal.more', { count: c.events.length - PER_DAY })}</span>}
          </div>
        ))}
      </div>
    </section>
  );
}
